import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { SxProps, useMediaQuery, useTheme } from '@mui/material';
import {
  ILayoutComponent,
  IStep,
  IWork,
  LEFT_DRAWER_MOBILE_BREAKPOINT,
  stepUtils,
  useNotifications,
  workflowEditQuery,
} from '@diana/core';
import { WORK_PAGE_NS } from '../pages/WorkPage/i18n';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useWorkflowSectionActions } from '../pages/WorkPage/DrawerTabs/Workflow/useWorkflowSectionActions';

export interface IUseSectionRename {
  component: ILayoutComponent;
  workId: string;
  work: IWork;
  step: IStep;
  index: number;
  useSectionActions: (props: IUseSectionActions) => any;
}

export interface IUseSectionActions {
  component: ILayoutComponent;
  setIsEditMode: (isEditable: boolean) => void;
  layoutId: string;
  workId: string;
  buttonSx?: SxProps;
  boxSx?: SxProps;
}

const EXCLUDED_SECTION_NAME_CHARACTERS_REGEX = /[`_=~!@#$%^&*()+[{\]}\\|;:'",?<.>/]/;

export const useSectionRename = (props: IUseSectionRename) => {
  const { component, step, workId, work, index } = props;
  const {
    breakpoints,
    shape: { borderRadius },
  } = useTheme();

  const { t } = useTranslation(WORK_PAGE_NS); // TODO: get t function from props
  const { error } = useNotifications();
  const defaultValue = component.label! ?? component.properties.label!;
  const isMobileDrawer = useMediaQuery(breakpoints.down(LEFT_DRAWER_MOBILE_BREAKPOINT));
  const isFirst = index === 0;
  const [layoutId, setLayoutId] = useState('');
  const [isValueValid, setIsValueValid] = useState(true);
  const [value, setValue] = useState<string>(defaultValue || t('section.untitledSection'));
  const [isEditMode, setEditMode] = useState<boolean>(false);
  const prevValue = useRef<string>('');
  const { canRead } = stepUtils.checkUserPermissions({ step, work });

  const updateSectionMutation = workflowEditQuery.renameSection();
  const { mutate: renameSection, isPending: isRenaming } = useMutation({
    mutationKey: updateSectionMutation.key,
    mutationFn: updateSectionMutation.fn,
    onSuccess: () => {
      prevValue.current = '';
    },
    onError: () => {
      setValue(prevValue.current);
      prevValue.current = '';
      error({
        message: t('errorMessage.cannotRenameSection'),
      });
    },
  });

  useEffect(() => {
    if (isRenaming) return;
    if (isEditMode) prevValue.current = value;
    else prevValue.current = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  useEffect(() => {
    if (step.layout?.id) {
      setLayoutId(step.layout.id);
    }
    if (step.events.length !== 0) {
      const currentFlowLayoutId = step.events[0].layoutId;
      setLayoutId(currentFlowLayoutId);
    }
  }, [step.events, component.canCloneComponent, component.canEditLabel, component, step.layout]);

  const setIsEditMode = useCallback(
    (state: boolean) => {
      if (!canRead) return;
      setEditMode(state);
    },
    [canRead],
  );

  const handleRename = useCallback(() => {
    if (prevValue.current === value) return;
    renameSection({
      rootLayoutId: layoutId,
      workId,
      component: component,
      titleValue: value,
    });
  }, [component, layoutId, renameSection, value, workId]);

  const handleSectionRenaming = useCallback(() => {
    if (isValueValid) {
      handleRename();
      setIsEditMode(false);
    }
  }, [handleRename, isValueValid, setIsEditMode]);

  const onEnterPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key.toLowerCase() === 'enter') {
        handleSectionRenaming();
      } else if (e.key.toLowerCase() === 'escape') {
        setValue(prevValue.current);
        setIsEditMode(false);
      }
    },
    [handleSectionRenaming, setIsEditMode],
  );

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    setIsValueValid(!Boolean(e.target.value.match(EXCLUDED_SECTION_NAME_CHARACTERS_REGEX)));
  }, []);

  const canEditLabel = component.canEditLabel;
  const onDoubleClick = useCallback(() => {
    if (canEditLabel) setIsEditMode(true);
  }, [canEditLabel, setIsEditMode]);

  const { menuActionsComponent, containerStyles } = useWorkflowSectionActions({
    component,
    setIsEditMode,
    layoutId,
    workId,
  });

  const isPreviewMode = step.userPermissions?.some((v) => v === 'PreviewMode');
  const children = isEditMode || !canRead || isPreviewMode ? null : <>{menuActionsComponent}</>;

  return {
    isFirst,
    isMobileDrawer,
    isEditMode,
    isValueValid,
    canRead,
    value,
    setIsEditMode,
    layoutId,
    borderRadius,
    handleRename,
    onEnterPress,
    handleSectionRenaming,
    handleChange,
    onDoubleClick,
    menuActionsComponent,
    containerStyles,
    children,
  };
};
